import { createTheme, ThemeOptions } from '@mui/material'
import { ExtendedTypographyOptions } from 'types/typographyClasses'

export const theme = createTheme({
  breakpoints: {
    values: {
      /**
       * 0 - 599: mobile
       * 600 - 959: tablet portrait
       * 960 - 1279: tablet landscape and laptop
       * 1280 - up: desktop
       */
      xl: 1440,
      lg: 1280,
      md: 960,
      xsm: 770,
      sm: 600,
      xs: 0
    }
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiInputBase: {
      defaultProps: {
        color: 'info'
      }
    },
    MuiTextField: {
      defaultProps: {
        color: 'info'
      }
    },
    MuiTypography: {
      defaultProps: {
        variant: 'smLight'
      }
    },
    /**
     * Tooltip que nao sai da tela.
     *
     * `placement="right"` aparece em ~70 lugares do projeto. No desktop cabe;
     * num celular de 360px o balao pede os 300px do `maxWidth` padrao a
     * direita do icone e nao ha esse espaco — ele era desenhado metade fora da
     * tela, ilegivel (os KPIs do Fluxo de Caixa eram o caso visivel). O `flip`
     * padrao do Popper so tenta o lado oposto, que na horizontal tambem nao
     * cabe, entao o balao ficava onde estava.
     *
     * As tres regras abaixo resolvem no tema em vez de em cada chamada:
     * - `fallbackPlacements` deixa o Popper descer para baixo/cima quando
     *   nenhum dos lados cabe;
     * - `preventOverflow` com `padding` segura o balao dentro da janela com
     *   uma folga da borda;
     * - `maxWidth` respeita a largura da tela quando ela e menor que 320px de
     *   conteudo.
     *
     * Onde ja cabia, nada muda: as tres so entram em acao no estouro.
     *
     * Ressalva: `defaultProps` e mesclado por prop, nao em profundidade, entao
     * um <Tooltip> que passe o proprio `slotProps` substitui este e volta ao
     * comportamento padrao do MUI. Sao 9 no projeto, nenhum deles com o balao
     * na lateral; se algum passar a estourar, o caminho e espalhar estas
     * opcoes na chamada.
     */
    MuiTooltip: {
      defaultProps: {
        slotProps: {
          popper: {
            modifiers: [
              {
                name: 'flip',
                options: { fallbackPlacements: ['bottom', 'top', 'left'] }
              },
              { name: 'preventOverflow', options: { padding: 8 } }
            ]
          },
          tooltip: {
            sx: { maxWidth: 'min(320px, calc(100vw - 24px))' }
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        row: {
          '&.Mui-selected': {
            backgroundColor: 'rgb(229, 247 ,228)',
            '&:hover': {
              backgroundColor: 'rgb(211, 244 ,210)'
            }
          },
          '&.error-row.Mui-selected': {
            backgroundColor: 'rgba(200, 0, 0, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(200, 0, 0, 0.3)'
            }
          },
          '&.error-row': {
            '&:hover': {
              backgroundColor: 'rgba(200, 0, 0, 0.1)'
            }
          }
        }
      }
    }
  },
  palette: {
    alert: {
      100: '#66471C',
      200: '#B27C30',
      300: '#F1A841',
      400: '#FFCC99',
      500: '#FFFCF7',
      main: '#FFCC99'
    },
    error: {
      100: '#4C000C',
      200: '#990017',
      300: '#CC2942',
      400: '#FF8080',
      500: '#FFFAFB',
      main: '#CC2942'
    },
    info: {
      100: '#294266',
      200: '#366AB2',
      300: '#4C98FF',
      400: '#B2D3FF',
      500: '#F7FBFF',
      main: '#3F403F'
    },
    neutral: {
      50: '#1E1E1E',
      100: '#231F20',
      200: '#3F403F',
      300: '#646664',
      400: '#777877',
      450: '#828282',
      500: '#A3A6A2',
      600: '#BEC0BE',
      700: '#E3E6E3',
      800: '#F2F5F2',
      900: '#F7FAF7',
      A100: '#FCFCFC',
      A200: '#FFFFFF',
      main: '#231F20'
    },
    primary: {
      100: '#3D892D',
      200: '#56BB4D',
      300: '#68E066',
      400: '#74FAA4',
      500: '#C3F9CC',
      600: '#E6FDEA',
      700: '#01F7C2',
      800: '#23ebc0',
      main: '#01F7C2'
    },
    secondary: {
      100: '#459CA0',
      200: '#59C6D3',
      300: '#65DFE9',
      400: '#6EF1F3',
      500: '#BCF4F5',
      550: '#00EC89',
      600: '#E4FBFC',
      main: '#459CA0'
    },
    success: {
      100: '#0F4C23',
      200: '#1A803C',
      300: '#30BF60',
      400: '#A9F2C1',
      500: '#F2FFF6',
      main: '#0F4C23'
    }
  },

  typography: {
    display: {
      color: '#3F403F',
      fontSize: '32px',
      fontWeight: 600,
      lineHeight: '120%',
      transition: 'font-size 200ms ease-in'
    },
    fontFamily: ['Inter', 'Roboto', 'sans-serif'].join(','),
    hero: {
      color: '#3F403F',
      fontSize: '80px',
      fontWeight: 600,
      lineHeight: '120%'
    },
    lgBold: {
      color: '#3F403F',
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '150%'
    },
    lgLight: {
      ['@media (min-width: 1280px)']: {
        fontSize: '24px'
      },
      color: '#3F403F',
      fontSize: '18px',
      fontWeight: 400,
      lineHeight: '150%',
      transition: 'font-size 200ms ease-in'
    },
    mdBold: {
      color: '#3F403F',
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '140%'
    },
    mdLight: {
      color: '#3F403F',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '140%'
    },
    menu: {
      color: '#777877',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '140%'
    },
    smBold: {
      color: '#3F403F',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '120%'
    },
    smLight: {
      color: '#3F403F',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '120%',
      textTransform: 'none'
    },
    xl: {
      color: '#3F403F',
      fontSize: '54px',
      fontWeight: 600,
      lineHeight: '120%'
    },
    xsBold: {
      color: '#3F403F',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '120%'
    },

    xsLight: {
      color: '#3F403F',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '120%',
      textTransform: 'none'
    }
  } as ExtendedTypographyOptions
} as ThemeOptions)
